<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

import { useApiCreateUserFavoriteAsset, useApiDeleteUserFavoriteAsset } from '~/apiClient';
import { type SaleItemEntity, type SaleListingItemEntity } from '~/types/sale.type';

type UiState = 'loading' | 'checked' | 'unchecked';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleListingItemEntity | SaleItemEntity>, required: true },
});

// EVENTS
const emit = defineEmits<{
    deletedFavorite: [{ saleId: string; assetId: string }];
}>();

// COMPOSABLES
const { isFavoriteAsset, isFavoriteAssetsInitialized, addFavoriteAsset, deleteFavoriteAsset } = useNuxtApp().$favoritesCache;
const { isLoggedIn } = useAuthUtils();
const { mutateAsync: mutateCreate } = useApiCreateUserFavoriteAsset();
const { mutateAsync: mutateDelete } = useApiDeleteUserFavoriteAsset();
const { notifySuccess, notifyGenericError } = useNotification();
const userGatedAction = useUserGatedActionState();
const { goToLoginPage } = useLoginRouteUtils();
const { tt } = useTypedI18n();

// DATA
const BUTTON_STATE_PROPS: Record<UiState, Pick<ButtonProps, 'color' | 'variant' | 'ui' | 'loading'>> = {
    loading: {
        color: 'neutral',
        variant: 'outline',
        ui: { base: 'border-1 border-neutral-200 ring-0 m-px' },
        loading: true,
    },
    checked: {
        color: 'primary',
        variant: 'solid',
        ui: { base: 'outline-2 outline-primary border-2 border-white hover:bg-primary-400 active:bg-primary-400' },
    },
    unchecked: {
        color: 'neutral',
        variant: 'outline',
        ui: { base: 'border-1 border-neutral-200 ring-0 m-px', leadingIcon: 'text-primary' },
    },
} as const;

const isLoading = ref(false);

const uiState = computed<UiState | undefined>(() => {
    if (!isLoggedIn.value) return 'unchecked';

    if (!isFavoriteAssetsInitialized.value) return;

    if (isLoading.value) return 'loading';

    return isFavoriteAsset(sale.mainAssetId) ? 'checked' : 'unchecked';
});

const buttonProps = computed(() => uiState.value && BUTTON_STATE_PROPS[uiState.value]);
const tooltipText = computed(() => uiState.value && tt(`favorites.tooltip.${uiState.value}`));

const mainAssetTitle = computed(() => `${sale.mainAsset.brand.brandValue} ${sale.mainAsset.model.modelValue}`);

// METHODS
const onAddFavorite = (): void => {
    isLoading.value = true;

    mutateCreate({ data: { assetIds: [sale.mainAssetId] } })
        .then(() => {
            addFavoriteAsset(sale.mainAssetId);
            notifySuccess({
                title: tt('favorites.notifications.add.title'),
                description: tt('favorites.notifications.add.description', { title: mainAssetTitle.value }),
            });
        })
        .catch(() => {
            notifyGenericError();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const onDeleteFavorite = (): void => {
    isLoading.value = true;

    mutateDelete({ data: { assetIds: [sale.mainAssetId] } })
        .then(() => {
            deleteFavoriteAsset(sale.mainAssetId);
            emit('deletedFavorite', { saleId: sale.id, assetId: sale.mainAssetId });
            notifySuccess({
                title: tt('favorites.notifications.delete.title'),
                description: tt('favorites.notifications.delete.description', { title: mainAssetTitle.value }),
            });
        })
        .catch(() => {
            notifyGenericError();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const onToggleFavorite = (): void => {
    if (!isLoggedIn.value) {
        userGatedAction.value = { action: UserGatedAction.ADD_FAVORITE_ASSET, payload: { assetId: sale.mainAssetId } };
        goToLoginPage();

        return;
    }

    if (!isFavoriteAssetsInitialized.value) return;

    isFavoriteAsset(sale.mainAssetId) ? onDeleteFavorite() : onAddFavorite();
};

// LIFECYCLE
onMounted(async () => {
    if (
        !isLoggedIn.value ||
        !userGatedAction.value ||
        userGatedAction.value.action !== UserGatedAction.ADD_FAVORITE_ASSET ||
        userGatedAction.value.payload.assetId !== sale.mainAssetId
    )
        return;

    userGatedAction.value = null;

    await until(isFavoriteAssetsInitialized).toBeTruthy();

    if (isFavoriteAsset(sale.mainAssetId)) return;

    onAddFavorite();
});
</script>

<template>
    <UTooltip
        :text="tooltipText"
        :content="{
            align: 'start',
            side: 'top',
        }"
        arrow
    >
        <UButton v-if="buttonProps" v-bind="buttonProps" icon="lucide:heart" square @click.prevent.stop="onToggleFavorite" />
    </UTooltip>
</template>
